<script setup lang="ts">
  import { BtnAiGradient, Modal, ModalSection } from '@gat-ship/vue-components'
  import { IconLoading, IconTrash, IconMagicWand } from '@gat-ship/icons'
  import {
    SmartPasteForm,
    useSmartPaste,
    usePromptAssets
  } from '@gat-ship/smart-paste'
  import type { ExtractionTarget } from '@gat-ship/types'
  import {
    isStakeholderContactPerson,
    isStakeholderContactOther
  } from '@gat-ship/types'
  import { ref, watch, useTemplateRef, computed } from 'vue'
  import { globalUtils } from '@gat-ship/utils'
  import ContactPersonListItem from '../list/ContactPersonListItem.vue'
  import ContactPointListItem from '../list/ContactPointListItem.vue'
  import type { StakeholderContact, StakeholderType } from '@gat-ship/types'
  import ContactsProvider from '../ContactsProvider.vue'
  import {
    useContactProvider,
    type ContactPersonContext
  } from '../../../composables/useStakeholderContacts'
  import {
    DataList,
    DataListItemAction,
    Button
  } from '@gat-ship/vue-components'

  const props = defineProps<{
    stakeholderId: string
    stakeholderName: string
    stakeholderShortName: string
    stakeholderType: StakeholderType
  }>()

  defineEmits<{
    (e: 'created', contacts: StakeholderContact[]): void
  }>()

  const stakeholderContactsProvider = useContactProvider()

  const stakeholderContacts = computed(
    () => stakeholderContactsProvider.contacts.value
  )

  const schemaId: ExtractionTarget = 'DE:Stakeholders.V1.CreateMultipleContacts'

  const promptAssets = usePromptAssets({
    target: 'DE:Stakeholders.CreateMultipleContacts',
    promptType: 'data-extraction'
  })

  const smartPaste = useSmartPaste({
    target: schemaId,
    autoReadClipboard: true
  })

  // The template ref points at the <ContactsProvider> child, which calls
  // `defineExpose(context)` with a `ContactPersonContext` shape. Typing the
  // ref explicitly is what lets every downstream `.value?.contacts.value`,
  // `.value?.setContacts`, `.value?.commands` access narrow to a real type
  // instead of `any` — without it, the file needed `@ts-nocheck` to compile.
  // Note the double `.value`: once to unwrap the template ref, once to
  // unwrap the inner `Ref<StakeholderContact[]>` that the provider exposes.
  const contactsProviderRef = useTemplateRef<ContactPersonContext>(
    'contactsProviderRef'
  )
  const contacts = computed(
    () => contactsProviderRef.value?.contacts.value ?? []
  )
  const isOpen = ref(false)

  const currentPage = ref<'smart-paste' | 'extracted'>('smart-paste')

  watch(isOpen, (newIsOpen) => {
    if (!newIsOpen) {
      contactsProviderRef.value?.setContacts([])
      currentPage.value = 'smart-paste'
    }
  })

  const isCreating = ref(false)

  async function handleGetContactsClick() {
    const data = await smartPaste.extract()
    if (data && 'contacts' in data) {
      const mappedContacts: StakeholderContact[] = data.contacts.map(
        (contact): StakeholderContact => {
          if (contact.contactType.value === 'person') {
            return {
              id: globalUtils.functions.uuid(),
              stakeholderId: props.stakeholderId,
              stakeholderName: props.stakeholderName,
              stakeholderShortName: props.stakeholderShortName,
              stakeholderType: props.stakeholderType,
              contactType: 'person',
              firstName: contact.firstName.value,
              lastName: contact.lastName.value,
              jobTitle: contact.jobTitle.value,
              email: contact.email.value,
              directLine: contact.directLine.value?.replaceAll(' ', '') ?? null,
              mobilePhone:
                contact.mobilePhone.value?.replaceAll(' ', '') ?? null,
              department: contact.department.value ?? null,
              isInactive: false
            }
          }
          return {
            id: globalUtils.functions.uuid(),
            stakeholderId: props.stakeholderId,
            stakeholderName: props.stakeholderName,
            stakeholderShortName: props.stakeholderShortName,
            stakeholderType: props.stakeholderType,
            contactType: 'other',
            otherName: contact.otherName.value,
            email: contact.email.value,
            directLine: contact.directLine.value?.replaceAll(' ', '') ?? null,
            mobilePhone: contact.mobilePhone.value?.replaceAll(' ', '') ?? null,
            isInactive: false
          }
        }
      )
      contactsProviderRef.value?.setContacts(mappedContacts)
      currentPage.value = 'extracted'
    }
  }

  function handleSmartPasteClick(event?: MouseEvent) {
    event?.preventDefault()
    event?.stopPropagation()
    isOpen.value = true
  }

  type ContactValidationError = { contactId: string; reason: string }

  /** Duplicate checks against the stakeholder's existing contacts. */
  function duplicateFieldErrors(
    contact: StakeholderContact
  ): ContactValidationError[] {
    const errors: ContactValidationError[] = []
    if (
      stakeholderContacts.value.some(
        (c) => c.email && c.email === contact.email
      )
    ) {
      errors.push({ contactId: contact.id, reason: 'Email already exists' })
    }
    if (
      stakeholderContacts.value.some(
        (c) => c.mobilePhone && c.mobilePhone === contact.mobilePhone
      )
    ) {
      errors.push({
        contactId: contact.id,
        reason: 'Mobile phone already exists'
      })
    }
    if (
      stakeholderContacts.value.some(
        (c) => c.directLine && c.directLine === contact.directLine
      )
    ) {
      errors.push({
        contactId: contact.id,
        reason: 'Direct line already exists'
      })
    }
    return errors
  }

  /** Required-name checks per contact-type variant. */
  function nameErrors(contact: StakeholderContact): ContactValidationError[] {
    const errors: ContactValidationError[] = []
    if (
      isStakeholderContactPerson(contact) &&
      (!contact.lastName || !contact.firstName)
    ) {
      errors.push({
        contactId: contact.id,
        reason: 'Last name and first name are required'
      })
    }
    if (isStakeholderContactOther(contact) && !contact.otherName) {
      errors.push({
        contactId: contact.id,
        reason: 'Other name is required'
      })
    }
    return errors
  }

  function validateContacts() {
    if (!contacts.value) {
      return {
        isValid: true,
        invalidContacts: []
      }
    }
    const errors = contacts.value.flatMap((contact) => [
      ...duplicateFieldErrors(contact),
      ...nameErrors(contact)
    ])
    if (errors.length > 0) {
      return {
        isValid: false,
        invalidContacts: errors
      }
    }
    return {
      isValid: true,
      invalidContacts: []
    }
  }

  const validationResult = computed(() => validateContacts())

  const contactsProvider = useContactProvider()

  async function handleCreateContactsClick() {
    const { isValid } = validateContacts()
    if (!isValid) {
      return
    }
    isCreating.value = true
    try {
      if (!contacts.value) {
        throw new Error('No contacts found')
      }
      const contactsToCreate = [...contacts.value]
      await contactsProvider.commands.addMultipleContacts(contactsToCreate)
      isOpen.value = false
      contactsProviderRef.value?.setContacts([])
    } catch (error) {
      isOpen.value = true
      throw error
    } finally {
      isCreating.value = false
    }
  }

  const showContactType = computed(() => {
    return (
      contacts.value?.some((contact) => isStakeholderContactPerson(contact)) &&
      contacts.value?.some((contact) => isStakeholderContactOther(contact))
    )
  })

  function handleContactClick(contact: StakeholderContact) {
    contactsProviderRef.value?.modals.showEditContactModal({
      stakeholderId: props.stakeholderId,
      stakeholderName: props.stakeholderName,
      stakeholderShortName: props.stakeholderShortName,
      stakeholderType: props.stakeholderType,
      contactType: contact.contactType,
      contact: contact
    })
  }
</script>
<template>
  <BtnAiGradient
    class="w-fit"
    label="Smart Paste"
    @click="handleSmartPasteClick"
  />
  <modal
    v-model:open="isOpen"
    title="Smart Paste Contacts"
    description="Use AI to create multiple contacts"
    type="dialog"
  >
    <ContactsProvider
      ref="contactsProviderRef"
      is-create
      :stakeholder-id="stakeholderId"
      :stakeholder-name="stakeholderName"
      :stakeholder-short-name="stakeholderShortName"
      :stakeholder-type="stakeholderType"
    >
      <template #default>
        <ModalSection v-if="currentPage === 'smart-paste'">
          <div
            v-if="smartPaste.isExtracting.value"
            class="flex items-center gap-2"
          >
            <IconLoading />
            <span class="text-sm font-medium">Extracting contacts...</span>
          </div>
          <SmartPasteForm
            v-else
            v-model:text="smartPaste.promptText.value"
            v-model:selected-asset-id="promptAssets.selectedAssetId.value"
            v-model:selected-thinking-model="
              promptAssets.selectedThinkingModel.value
            "
            :assets="promptAssets.assets.value"
            :can-manage-assets="promptAssets.canManageAssets.value"
            :can-override-model="promptAssets.canOverrideModel.value"
          />
        </ModalSection>
        <ModalSection v-else-if="currentPage === 'extracted'">
          <DataList>
            <template v-for="contact in contacts" :key="contact.id">
              <ContactPersonListItem
                v-if="isStakeholderContactPerson(contact)"
                :contact="contact"
                :error-messages="
                  validationResult.invalidContacts
                    .filter((c) => c.contactId === contact.id)
                    .map((c) => c.reason)
                "
                :show-contact-type="showContactType"
                @click:item="handleContactClick"
              >
                <template #actions>
                  <DataListItemAction
                    :icon="IconTrash"
                    @click.stop="
                      contactsProviderRef?.commands.deleteContact(contact)
                    "
                  />
                </template>
              </ContactPersonListItem>
              <ContactPointListItem
                v-else-if="isStakeholderContactOther(contact)"
                :contact="contact"
                :show-contact-type="showContactType"
                :error-messages="
                  validationResult.invalidContacts
                    .filter((c) => c.contactId === contact.id)
                    .map((c) => c.reason)
                "
                @click:item="handleContactClick"
              >
                <template #actions>
                  <DataListItemAction
                    :icon="IconTrash"
                    @click.stop="
                      contactsProviderRef?.commands.deleteContact(contact)
                    "
                  />
                </template>
              </ContactPointListItem>
            </template>
          </DataList>
        </ModalSection>
      </template>
    </ContactsProvider>
    <template #footer>
      <ModalSection class="flex flex-row gap-2 p-5">
        <div v-if="!validationResult.isValid" class="flex items-center">
          <span class="text-error text-xs">
            Please fix the errors in the contacts list
          </span>
        </div>
        <Button class="ml-auto" variant="ghost" @click="isOpen = false">
          Cancel
        </Button>
        <Button
          v-if="contacts && Array.isArray(contacts) && contacts.length > 0"
          :disabled="!validateContacts().isValid"
          @click="handleCreateContactsClick"
        >
          <IconMagicWand class="size-4" />
          Create Contacts
        </Button>
        <Button
          v-else-if="!smartPaste.isExtracting.value"
          @click="handleGetContactsClick"
        >
          <IconMagicWand class="size-4" />
          Get Contacts
        </Button>
      </ModalSection>
    </template>
  </modal>
</template>
